import { useState, useEffect, useMemo, useRef } from 'react';
import TextField from '@mui/material/TextField';
import { Box, InputAdornment, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faLocationDot,
  faLocationArrow,
} from '@fortawesome/free-solid-svg-icons';
import { searchLocation, Location } from '../services/profile';
import { reverseGeoCode } from '../../community/service/PlaceService';
import { PlaceData } from '../../community/model/Places';
import { styled } from '@mui/material/styles';
import { FormattedMessage, useIntl } from 'react-intl';

const ProfileTextField = styled(TextField)({
  '& .MuiInputBase-root': {
    backgroundColor: '#fff',
    borderRadius: '12px',
  },
  '& fieldset': {
    borderRadius: '12px',
  },
});

function getCurrentLocation(): Promise<PlaceData | null> {
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      async function (geo: GeolocationPosition) {
        const place = await reverseGeoCode(
          geo.coords.latitude,
          geo.coords.longitude,
        );
        resolve(place as PlaceData);
      },
      function (errorPosition: GeolocationPositionError) {
        console.warn('Failed to get current location', errorPosition);
        resolve(null);
      },
      { timeout: 30_000 },
    );
  });
}

const borderRadius = '12px';

export function LocationSearch(props: {
  value: string;
  onChange?: (value: string) => void;
}) {
  const [term, setTerm] = useState('');

  const [initialized, setInitialize] = useState(false);
  const [options, setOptions] = useState<readonly Location[]>([]);
  const [inputValue, setInputValue] = useState('');

  const [inputFocus, setInputFocus] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    function onFocus() {
      setInputFocus(true);
    }

    const ref = inputRef.current;

    if (!ref) return;
    ref.addEventListener('focus', onFocus);

    return () => {
      ref.removeEventListener('focus', onFocus);
    };
  }, [inputRef]);

  const intl = useIntl();
  const requiredError = useMemo(
    () =>
      intl.formatMessage({
        id: 'common.form.field.required',
      }),
    [intl],
  );

  if (!initialized && props.value && props.value !== inputValue) {
    setInputValue(() => props.value);
    setInitialize(true);
  }

  useEffect(() => {
    if (!term) return setOptions([]);
    (async () => {
      const locations = await searchLocation(term);
      setOptions(locations);
    })();
  }, [term]);

  function renderList() {
    return (
      <Box
        sx={{
          boxShadow:
            '0 12px 16px 0 rgba(59, 69, 89, 0.05), 0 8px 10px 0 rgba(59, 69, 89, 0.1)',
          background: '#fff',
          overflowY: 'auto',
          borderBottomLeftRadius: 12,
          borderBottomRightRadius: 12,
        }}
      >
        {options.map((location) => (
          <Box
            key={location.place_id}
            sx={{
              cursor: 'pointer',
              px: 5,
            }}
            onClick={() => {
              setInputFocus(false);
              setInputValue(location.description);
              props.onChange && props.onChange(location.description);
              setTerm('');
            }}
          >
            <Box sx={{ borderBottom: 'solid 0.5px #d8dade', px: 6.5, py: 5 }}>
              <Typography variant={'body1'}>{location.description}</Typography>
            </Box>
          </Box>
        ))}
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative' }}>
      <ProfileTextField
        sx={{ width: 1 }}
        value={inputValue}
        error={!inputValue}
        helperText={term || inputValue ? '' : requiredError}
        inputRef={inputRef}
        InputProps={{
          startAdornment: (
            <InputAdornment position='start'>
              <Box sx={{ marginLeft: 2.5 }}>
                <FontAwesomeIcon
                  icon={faLocationDot}
                  color='#307fe2'
                  size='lg'
                />
              </Box>
            </InputAdornment>
          ),
        }}
        onChange={(event) => {
          setInputValue(event.target.value);
          setTerm(event.target.value);
        }}
      />

      {(inputFocus || term) && (
        <Box
          sx={{
            mt: term ? 5 : 2,
            width: '100%',
            position: 'absolute',
            overflow: 'hidden',
            zIndex: 1000,
            borderRadius: borderRadius,
            backgroundColor: '#fff',
            border: 'solid 0.5px #d8dade',
            boxShadow:
              '0 12px 16px 0 rgba(59, 69, 89, 0.05), 0 8px 10px 0 rgba(59, 69, 89, 0.1)',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              borderTopRightRadius: borderRadius,
              borderTopLeftRadius: borderRadius,
              borderBottomLeftRadius: options.length ? 0 : borderRadius,
              borderBottomRightRadius: options.length ? 0 : borderRadius,
              px: 5,
              py: 5,
              cursor: 'pointer',
              borderBottom: 'solid 0.5px #d8dade',
            }}
            onClick={async () => {
              setInputFocus(false);
              const location = await getCurrentLocation();
              const address = location?.formattedAddress ?? '';
              setInputValue(address);
              props.onChange && props.onChange(address);
              setTerm('');
            }}
          >
            <FontAwesomeIcon icon={faLocationArrow} color='#3b4559' size='lg' />
            <Typography variant={'body1'} sx={{ ml: 2.5 }}>
              <FormattedMessage id='community.filter.location.current-location' />
            </Typography>
          </Box>

          {renderList()}
        </Box>
      )}
    </Box>
  );
}
